import React, { useState } from 'react';
import './Contact.css';
import { submitContactRequest } from '../utils/formSubmission';

/* ── Icons ─────────────────────────────────────────── */
const MailIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1b9fcf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1b9fcf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18a2 2 0 0 1 1.99-2.18h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.37a16 16 0 0 0 6.72 6.72l1.63-1.63a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#1b9fcf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#1b9fcf" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

/* ── Contact Info items ────────────────────────────── */
const CONTACT_INFO = [
  { Icon: MailIcon, label: 'Email', value: 'tharenterprisespune@gmail.com' },
  { Icon: PhoneIcon, label: 'Phone', value: '+91 96642 23533' },
  { Icon: MapPinIcon, label: 'Address', value: ' Office No 2-3, Shreyas Corner,Opp Union Bank, Nanded Phata,Pune-68' },
];

const BUSINESS_HOURS = [
  { day: 'Monday - Friday', time: '9:00 AM - 6:30 PM' },
  { day: 'Saturday', time: '9:00 AM - 6:00 PM' },
  { day: 'Sunday', time: 'Closed' },
];

/* ── Form initial state ────────────────────────────── */
const INITIAL_FORM = { name: '', email: '', subject: '', message: '' };

/* ── Contact Component ─────────────────────────────── */
const Contact = () => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = (fields = form) => {
    const errs = {};
    // Name: required, max 20
    if (!fields.name.trim()) {
      errs.name = 'Name is required.';
    } else if (fields.name.length > 20) {
      errs.name = 'Name must be at most 20 characters.';
    }
    // Email: required, valid
    if (!fields.email.trim()) {
      errs.email = 'Email is required.';
    } else if (!/^\S+@\S+\.\S+$/.test(fields.email)) {
      errs.email = 'Enter a valid email address.';
    }
    // Subject: required, max 50
    if (!fields.subject.trim()) {
      errs.subject = 'Subject is required.';
    } else if (fields.subject.length > 50) {
      errs.subject = 'Subject must be at most 50 characters.';
    }
    // Message: optional, max 100
    if (fields.message && fields.message.length > 100) {
      errs.message = 'Message must be at most 100 characters.';
    }
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const errs = validate();
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    const send = async () => {
      try {
        setIsSubmitting(true);
        await submitContactRequest(form);
        setSubmitted(true);
        setForm(INITIAL_FORM);
        setErrors({}); // clear errors after successful submit
        setTimeout(() => setSubmitted(false), 4000);
      } catch (error) {
        alert(error.message || 'Failed to send message. Please try again later.');
        console.error('Contact send error:', error);
      } finally {
        setIsSubmitting(false);
      }
    };
    send();
  };

  return (
    <section id="contact" className="section contact">
      <div className="container">
        <h2 className="section-title">Get in Touch</h2>
        <p className="section-subtitle">
          Have questions? We're here to help you with all your business registration needs
        </p>

        <div className="contact__grid">
          {/* Left column */}
          <div className="contact__left">
            {/* Contact Info Card */}
            <div className="contact__card">
              <h3 className="contact__card-title">Contact Information</h3>
              <ul className="contact__info-list">
                {CONTACT_INFO.map(({ Icon, label, value }) => (
                  <li key={label} className="contact__info-item">
                    <div className="contact__info-icon">
                      <Icon />
                    </div>
                    <div>
                      <span className="contact__info-label">{label}</span>
                      <span className="contact__info-value">{value}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Business Hours Card */}
            <div className="contact__hours-card">
              <h3 className="contact__hours-title">
                <ClockIcon /> Business Hours
              </h3>
              <ul className="contact__hours-list">
                {BUSINESS_HOURS.map(({ day, time }) => (
                  <li key={day} className="contact__hours-item">
                    <span className="contact__hours-day">{day}:</span>
                    <span className={`contact__hours-time${time === 'Closed' ? ' contact__hours-time--closed' : ''}`}>
                      {time}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right column: Message Form */}
          <div className="contact__card contact__form-card">
            <h3 className="contact__card-title">Send us a Message</h3>

            {submitted && (
              <div className="contact__success">
                ✓ Message sent! We'll get back to you soon.
              </div>
            )}

            <form className="contact__form" onSubmit={handleSubmit} noValidate>
              <input
                className="contact__input"
                type="text"
                name="name"
                placeholder="Your Name"
                value={form.name}
                onChange={handleChange}
                required
                maxLength={20}
              />
              {errors.name && <div className="contact__error">{errors.name}</div>}
              <input
                className="contact__input"
                type="email"
                name="email"
                placeholder="Your Email"
                value={form.email}
                onChange={handleChange}
                required
              />
              {errors.email && <div className="contact__error">{errors.email}</div>}
              <input
                className="contact__input"
                type="text"
                name="subject"
                placeholder="Subject"
                value={form.subject}
                onChange={handleChange}
                required
                maxLength={50}
              />
              {errors.subject && <div className="contact__error">{errors.subject}</div>}
              <textarea
                className="contact__input contact__textarea"
                name="message"
                placeholder="Your Message (optional)"
                value={form.message}
                onChange={handleChange}
                rows={5}
                maxLength={100}
              />
              {errors.message && <div className="contact__error">{errors.message}</div>}
              <button className="contact__submit" type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
